/**
 * Maps the inline Markdown links in the assistant's answers back to the full
 * post objects from the client-side destination collection, so the conversation
 * can render rich blurbs alongside the text.
 *
 * The assistant grounds its answers in real pages and links to them inline
 * (e.g. `[Foldvik Familiepark](/foldvik-familiepark)`). Those same pages are
 * already loaded in the browser via <DesitnationCollectionProvider> (products,
 * events, pages), so we match by URL path instead of plumbing post ids through
 * the chat stream.
 */

/** Normalise any URL or path to a comparable pathname (no origin, no trailing slash). */
function pathOf(url) {
  if (!url) return '';
  try {
    const u = /^https?:\/\//.test(url) ? new URL(url) : new URL(url, 'https://local.invalid');
    return u.pathname.replace(/\/+$/, '') || '/';
  } catch {
    return String(url).split(/[?#]/)[0].replace(/\/+$/, '');
  }
}

/** Extract the hrefs of all Markdown links `[label](href)` in a chunk of text. */
function markdownLinkHrefs(text) {
  const hrefs = [];
  const re = /\[[^\]]*\]\(\s*([^)\s]+)(?:\s+"[^"]*")?\s*\)/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    hrefs.push(match[1]);
  }
  return hrefs;
}

/** Concatenate the text parts of a UI message. */
function messageText(message) {
  return (message.parts ?? [])
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('\n');
}

/**
 * Index a collection (products/events/pages) by URL path, for O(1) href lookups.
 * @param {Array} collection - posts with a `url`/`permalink` field
 * @returns {Map<string, object>} path → post
 */
export function buildPostIndex(collection = []) {
  const byPath = new Map();
  for (const post of collection) {
    const key = pathOf(post?.url || post?.permalink);
    // First post wins for a given path (collections shouldn't collide anyway).
    if (key && !byPath.has(key)) byPath.set(key, post);
  }
  return byPath;
}

/** Resolve a single link href to a post from a prebuilt index (or null). */
export function lookupPostByHref(index, href) {
  if (!index || !href) return null;
  return index.get(pathOf(href)) ?? null;
}

/**
 * Guard against links the assistant invented.
 *
 * The model occasionally rewrites a url instead of copying it — most often by
 * translating the slug into the language it is answering in, so a Swedish answer
 * turns "/arrangementer/charlie-og-sjokoladefabrikken" into "...-och-...". The
 * page does not exist, and no instruction has reliably stopped it, so the answer
 * is checked against reality here instead: a link into a section we hold the
 * complete list for, pointing at something not in it, cannot be real.
 *
 * Deliberately narrow. Only sections that appear as "/section/slug" in the
 * loaded collection are policed — a link to a category landing page, an article,
 * or an external booking system is none of our business and passes untouched.
 *
 * @param {Map<string, object>} index - path → post, from buildPostIndex()
 * @returns {(href: string) => boolean} true when the href cannot resolve
 */
export function buildDeadLinkCheck(index) {
  const sections = new Set();
  for (const path of index?.keys() ?? []) {
    const [, section, slug] = path.split('/');
    if (section && slug) sections.add(section);
  }

  return (href) => {
    if (!href || !sections.size) return false;
    const path = pathOf(href);
    const [, section, slug] = path.split('/');
    // Only "/section/slug" inside a section we can vouch for in full.
    if (!section || !slug || !sections.has(section)) return false;
    return !index.has(path);
  };
}

/**
 * The posts the assistant has linked to across the whole conversation, in the
 * order first mentioned, de-duplicated. `collection` is the merged list of
 * products/events/pages from useDesitnationCollection().
 *
 * @param {Array} messages - useChat() messages
 * @param {Array} collection - posts with a `url`/`permalink` field
 * @returns {Array} matched post objects
 */
export function mentionedPosts(messages = [], collection = []) {
  const index = buildPostIndex(collection);
  if (!index.size) return [];

  const seen = new Set();
  const result = [];
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    for (const href of markdownLinkHrefs(messageText(message))) {
      const post = lookupPostByHref(index, href);
      if (!post) continue;
      const id = post.post_id ?? post.id ?? pathOf(href);
      if (seen.has(id)) continue;
      seen.add(id);
      result.push(post);
    }
  }
  return result;
}

/**
 * The posts a single assistant message links to, in mention order, de-duplicated.
 * Lets each answer render its own blurb list (products/events/pages it links to)
 * right beneath the text, instead of one shared list for the conversation.
 *
 * @param {object} message - a useChat() message
 * @param {(href: string) => object|null} lookup - href → post resolver (lookupPostByHref bound to an index)
 * @returns {Array} matched post objects
 */
export function mentionedPostsForMessage(message, lookup) {
  if (!message || message.role !== 'assistant' || typeof lookup !== 'function') return [];

  const seen = new Set();
  const result = [];
  for (const href of markdownLinkHrefs(messageText(message))) {
    const post = lookup(href);
    if (!post) continue;
    const id = post.post_id ?? post.id ?? pathOf(href);
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(post);
  }
  return result;
}
